import { ref, computed } from 'vue';
import { remoteConfig, getValue, safeFetchAndActivate, isQuotaExceededError } from '@/firebase';
import useUserStore from '@/store/user';

export interface DeveloperTestStream {
  matchId: string;
  channel?: string;
  matchTitle?: string;
  streamType?: string;
  maxBitrate?: string;
  coverKey?: string;
  tokenUrl?: string;
}

const DEFAULT_TEST_STREAMS: DeveloperTestStream[] = [
  {
    matchId: '123456',
    channel: 'ch09',
    matchTitle: '_',
    streamType: '_',
    maxBitrate: '_',
    coverKey: '',
    tokenUrl: '',
  },
];
const developerIds = ref<string[]>([]);
const testStreams = ref<DeveloperTestStream[]>(DEFAULT_TEST_STREAMS);
let isInitialized = false;

function normalizeTestStreams(raw: unknown): DeveloperTestStream[] {
  if (!Array.isArray(raw)) return DEFAULT_TEST_STREAMS;

  const normalized = raw
    .map((item: any) => ({
      matchId: String(item?.matchId ?? '').trim(),
      channel: item?.channel == null ? '' : String(item.channel).trim(),
      matchTitle: item?.matchTitle == null ? '_' : String(item.matchTitle).trim() || '_',
      streamType: item?.streamType == null ? '_' : String(item.streamType).trim() || '_',
      maxBitrate: item?.maxBitrate == null ? '_' : String(item.maxBitrate).trim() || '_',
      coverKey: item?.coverKey == null ? '' : String(item.coverKey).trim(),
      tokenUrl: item?.tokenUrl == null ? '' : String(item.tokenUrl).trim(),
    }))
    .filter(item => item.matchId);

  return normalized.length > 0 ? normalized : DEFAULT_TEST_STREAMS;
}

/**
 * Fetches the developer IDs from Firebase Remote Config
 */
async function fetchDeveloperIds(): Promise<void> {
  if (isInitialized) return;

  try {
    // Fetch and activate remote config with quota error handling
    const success = await safeFetchAndActivate();
    if (!success) {
      console.warn(
        '[DeveloperCheck] Failed to fetch remote config (quota exceeded or error). Using empty developer list.',
      );
      developerIds.value = [];
      testStreams.value = DEFAULT_TEST_STREAMS;
      isInitialized = true;
      return;
    }

    // Get the developers config value
    const developersConfigValue = getValue(remoteConfig, 'developers');
    const developersJson = developersConfigValue.asString();

    console.log('[DeveloperCheck] Raw developers config:', developersJson);

    if (developersJson) {
      const developersConfig = JSON.parse(developersJson);
      developerIds.value = developersConfig.ids || [];
      testStreams.value = normalizeTestStreams(developersConfig.testStreams);
      console.log('[DeveloperCheck] Developer IDs loaded:', developerIds.value);
      console.log('[DeveloperCheck] Test streams loaded:', testStreams.value);
    } else {
      console.warn('[DeveloperCheck] No developers config found in Remote Config');
      developerIds.value = [];
      testStreams.value = DEFAULT_TEST_STREAMS;
    }

    isInitialized = true;
  } catch (error: any) {
    if (isQuotaExceededError(error)) {
      console.error('[DeveloperCheck] Quota exceeded error while fetching developer IDs. Using empty list.');
    } else {
      console.error('[DeveloperCheck] Error fetching developer IDs:', error);
    }
    developerIds.value = [];
    testStreams.value = DEFAULT_TEST_STREAMS;
    isInitialized = true;
  }
}

/**
 * Composable to check if the current user is a developer
 */
export function useDeveloperCheck() {
  const userStore = useUserStore();

  // Computed property to check if current user is a developer
  const isDeveloper = computed(() => {
    const firebaseId = userStore.userInfo?.firebase_id;

    console.log('[DeveloperCheck] ===== DEVELOPER CHECK DEBUG =====');
    console.log('[DeveloperCheck] User info:', userStore.userInfo);
    console.log('[DeveloperCheck] Firebase ID from user:', firebaseId);
    console.log('[DeveloperCheck] Developer IDs list:', developerIds.value);
    console.log('[DeveloperCheck] Is initialized:', isInitialized);

    if (!firebaseId) {
      console.warn('[DeveloperCheck] No Firebase ID found for user!');
      return false;
    }

    const result = developerIds.value.includes(firebaseId);
    console.log('[DeveloperCheck] Includes check result:', result);
    console.log('[DeveloperCheck] ===== END DEBUG =====');

    return result;
  });

  return {
    isDeveloper,
    fetchDeveloperIds,
    developerIds,
    testStreams,
  };
}
